import React, { CSSProperties, ReactNode, useContext } from "react";
import LinkButton from "common/Buttons/LinkButton";
import { AnalyticsContext } from "analytics/AnalyticsProvider";
import { ActionTooltip } from "common/Tooltips/ActionTooltip";
import styled from "styled-components";
import Button from "common/Buttons/Button";
import {
  FollowSignalType,
  SignalTableActionButtonProps,
} from "@shared/features/signals/common/types";
import useFollowSignalActionButton from "features/signals/SignalFollow/useFollowSignalActionButton.tsx";
import { TooltipPlacement } from "antd/es/tooltip";
import NoExchangeConnectedModal from "features/configuration/common/NoExchangeConnectedModal";

interface WebSignalTableActionButtonProps extends SignalTableActionButtonProps {
  tooltipPlacement?: TooltipPlacement;
  buttonStyle?: CSSProperties;
  icon?: ReactNode;
  isCard?: boolean;
  color?: string;
  showFullText?: boolean;
}

export const SignalFollowActionButton = ({
  signal,
  followType,
  tooltipPlacement,
  isDrawer,
  isCard,
  buttonStyle,
  icon,
  color,
  showFullText,
}: WebSignalTableActionButtonProps) => {
  const { analytics } = useContext(AnalyticsContext);
  const {
    onClick,
    disabled,
    text,
    hoverMessage,
    noExchangeModalVisible,
    setNoExchangeModalVisible,
    showNoExchangeError,
  } = useFollowSignalActionButton({
    signal,
    followType,
    onOpenModal: () => {
      const analyticsArgs = {
        groupSignalId: signal.signal_id,
        groupChannelId: signal.sub_group_id,
      };
      if (followType === FollowSignalType.FOLLOW)
        analytics?.oneClickFollowSignalClicked(analyticsArgs);
      else if (followType === FollowSignalType.UPDATE)
        analytics?.updateTradeBasedOnSignalClicked(analyticsArgs);
      else if (followType === FollowSignalType.CANCEL)
        analytics?.closeTradeBasedOnSignalClicked(analyticsArgs);
    },
  });

  const button = (
    <Button onClick={onClick} $error={disabled} style={buttonStyle}>
      {icon}
      {text}
    </Button>
  );

  const linkButton = (
    <StyledLinkButton
      onClick={onClick}
      type="link"
      $error={disabled}
      style={buttonStyle}
      $color={color}
    >
      {icon}
      {showFullText ? hoverMessage : text}
    </StyledLinkButton>
  );

  const getButton = () => {
    if (isCard) return linkButton;
    else if (isDrawer) return button;
    return (
      <ActionTooltip
        title={hoverMessage}
        placement={tooltipPlacement}
        hideMobile={isDrawer}
      >
        {linkButton}
      </ActionTooltip>
    );
  };

  return (
    <div
      onClick={(e) => {
        if (!isCard) e.stopPropagation();
      }}
    >
      {getButton()}
      {showNoExchangeError && (
        <NoExchangeConnectedModal
          placement="signal_follow"
          open={noExchangeModalVisible}
          onClose={() => setNoExchangeModalVisible(false)}
          action="follow signals"
        />
      )}
    </div>
  );
};

const StyledLinkButton = styled(LinkButton)<{
  $bordered?: boolean;
}>`
  height: 28px;
  padding: 0;
  ${(p) =>
    p.$bordered
      ? `
  border: 1px solid;
  padding: 0 8px;
  &:hover, &:focus {
    border-color:initial;
  }`
      : ""}
`;
